'use client';

import Dialog from '@/components/Dialog';
import Popup from '@/components/Popup';
import { toast } from '@/components/Toast';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

// 跟踪信息类型定义

interface TrackOrderDialogProps {
  show: boolean;
  onClose: () => void;
  orderDetail?: any;
}

export default function TrackOrderDialog({ show, onClose, orderDetail }: TrackOrderDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const t = useTranslations('account.myOrders.orderDetail.trackDetail');
  const [loading] = useState(false);

  // 复制跟踪号
  const handleCopy = () => {
    const trackNumber = orderDetail?.track_number || orderDetail?.shipment_number;
    if (trackNumber) {
      navigator.clipboard.writeText(trackNumber);
      toast.success(t('copySuccess'));
    }
  };

  // 渲染跟踪信息
  const renderTrackingInfo = () => {
    const hasTrackingData =
      (orderDetail.order_ships && orderDetail.order_ships.length > 0) ||
      (orderDetail.head_ships && orderDetail.head_ships.length > 0) ||
      (orderDetail.order_node_val && orderDetail.order_node_val.length > 0);
    const shouldShowFirstTrackItem = !!(
      orderDetail.carrier_infos?.status ||
      orderDetail.carrier_infos?.sub_status_info ||
      orderDetail.carrier_infos?.delivery_time
    );

    if (!hasTrackingData) {
      return (
        <div className="text-center py-[40px] text-[#666]">
          No information available, please try again later.
        </div>
      );
    }

    return (
      <div className="track-content md:overflow-y-auto min-h-[100px] mr-[4px] pb-[20px] max-h-[410px]">
        <ul className="track-wrap md:mx-[24px] pb-[60px] md:pb-0">
          {/* 第一个特殊项目 - 承运商信息 */}
          {shouldShowFirstTrackItem && (
            <li className="track-item-content-first relative ml-[22px] py-[10px] leading-[22px] text-[#333] before:content-[''] before:absolute before:left-[-22px] before:top-[26px] before:w-[10px] before:h-[10px] before:mt-[-12px] before:bg-primary-purple before:rounded-full after:content-[''] after:absolute after:left-[-17px] after:top-[27px] after:w-[1px] after:h-[75%] after:min-h-[36px] after:border-l after:border-dashed after:border-[#D0D4DB]">
              {orderDetail.carrier_infos.status && (
                <div className="track-item-content-title text-primary-purple font-be mb-[5px]">
                  {orderDetail.carrier_infos.status}
                </div>
              )}
              {orderDetail.carrier_infos.sub_status_info && (
                <div className="track-item-content-sub-status text-[#333] mb-[5px]">
                  {orderDetail.carrier_infos.sub_status_info}
                </div>
              )}
              {orderDetail.carrier_infos.delivery_time && (
                <div className="track-item-content-delivery-time text-[#333]">
                  Estimated latest delivery time is {orderDetail.carrier_infos.delivery_time}
                </div>
              )}
            </li>
          )}

          {/* 所有跟踪节点 - 合并order_ships、head_ships和order_node_val */}
          {[
            ...(orderDetail.order_ships || []),
            ...(orderDetail.head_ships || []),
            ...(orderDetail.order_node_val || []),
          ].map((item, index) => (
            <li
              key={item.id || index}
              className={`track-item relative ml-[22px] py-[10px] leading-[22px] text-[#666] before:content-[''] before:absolute before:left-[-22px] before:top-[26px] before:w-[10px] before:h-[10px] before:mt-[-12px] ${index === 0 && !shouldShowFirstTrackItem ? 'before:bg-primary-purple' : 'before:bg-[#D0D4DB]'} before:rounded-full after:content-[''] after:absolute after:left-[-17px] after:top-[27px] after:w-[1px] after:h-[75%] after:min-h-[36px] after:border-l after:border-dashed after:border-[#D0D4DB] last:after:h-[16px]`}
            >
              <div className="track-item-content flex flex-col w-full">
                {item.created_at && (
                  <time
                    className={`mr-[10px] text-[12px] md:text-[14px] mb-[2px] ${index === 0 && !shouldShowFirstTrackItem ? 'text-primary-text md:font-bb' : 'text-[#a9a9a9]'}`}
                  >
                    {item.created_at}
                  </time>
                )}
                <span
                  className={`text-[12px] md:text-[14px] ${index === 0 && !shouldShowFirstTrackItem ? 'text-primary-text md:font-bb' : 'text-[#a9a9a9]'}`}
                >
                  {item.content}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (!orderDetail && !loading) {
    return null;
  }

  const renderContent = () => (
    <div className="track-order-dialog">
      {/* 内容 */}
      <div className="text-[14px]">
        {/* Loading状态 */}
        {loading && (
          <div className="flex items-center justify-center py-[40px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-purple" />
          </div>
        )}

        {/* 无数据状态 */}
        {!loading && !orderDetail && (
          <div className="text-center py-[40px] text-[#666]">
            No information available, please try again later.
          </div>
        )}

        {/* 有数据时显示订单详情 */}
        {!loading && orderDetail && (
          <div>
            {/* 顶部信息 */}
            <div>
              {/* 通知信息 */}
              {orderDetail.notice_message && (
                <div className="flex flex-col mb-[20px] mr-[20px] text-[14px] text-[#666]">
                  {orderDetail.notice_message}
                </div>
              )}

              {/* 跟踪号 */}
              {(orderDetail.track_number || orderDetail.shipment_number) && (
                <div className="flex relative items-center mb-[24px] md:mb-0 md:mt-[10px] md:mx-[24px]">
                  <div className="text-[16px] font-bb">
                    Track No.&nbsp;
                    <br className="md:hidden" />
                    <span>{orderDetail.track_number || orderDetail.shipment_number}</span>
                  </div>
                  <div
                    className="absolute md:static right-0 bottom-0 ml-[8px] text-[14px] text-primary-purple underline cursor-pointer"
                    onClick={handleCopy}
                  >
                    Copy
                  </div>
                </div>
              )}

              {/* 物流渠道 */}
              {orderDetail.logistics_channel_href && orderDetail.agent_logistics_code && (
                <div className="text-[12px] md:mx-[24px]">
                  <span>Logistics Channel: </span>
                  <a
                    href={orderDetail.logistics_channel_href}
                    className="text-primary-purple underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {orderDetail.agent_logistics_code}
                  </a>
                </div>
              )}
            </div>

            {/* 跟踪信息 */}
            {renderTrackingInfo()}
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Popup
        open={show}
        onClose={onClose}
        position="bottom"
        height="74vh"
        title={orderDetail.notice_message || t('title')}
        closeOnClickOverlay
        sx={{
          '& .popup-content': {
            paddingLeft: '12px',
            paddingRight: '12px',
          },
        }}
      >
        {renderContent()}
      </Popup>
    );
  }

  return (
    <Dialog
      open={show}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      footer={null}
      showTitleDivider
      closeOnClickOverlay={!!isMobile}
      title={orderDetail.notice_message || t('title')}
      sx={{
        '& .MuiDialog-paper': {
          width: '600px',
          maxWidth: '100%',
        },
        '& .MuiDialogContent-root': {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }}
    >
      {renderContent()}
    </Dialog>
  );
}
